import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { CONSENT_BANNER, LegalNodes } from '@/content/legal';
import { consent as defaultConsent, type ConsentApi } from '@/lib/measurement';

interface ConsentBannerViewProps {
  readonly onAllow: () => void;
  readonly onRefuse: () => void;
}

/**
 * The measurement consent bar, markup as production (legal-copy.md §1) except ML-D14: an
 * accessible, labelled, non-modal region instead of a `role="dialog"` that managed nothing.
 * Its buttons are ordinary tab stops; it never traps focus and never blocks the page.
 */
export function ConsentBannerView({ onAllow, onRefuse }: ConsentBannerViewProps) {
  return (
    <div id="m618-consent" role="region" aria-label={CONSENT_BANNER.label}>
      <p>
        <LegalNodes nodes={CONSENT_BANNER.message} />
      </p>
      <div className="m618-btns">
        <button className="m618-yes" type="button" onClick={onAllow}>
          {CONSENT_BANNER.allow}
        </button>
        <button type="button" onClick={onRefuse}>
          {CONSENT_BANNER.refuse}
        </button>
      </div>
    </div>
  );
}

const closed = () => false;

/**
 * Mounted once by the App shell. Shown when measurement boots without a stored choice in a
 * consent region, and whenever the choice is revoked (Cookie choice on /privacy), in any region.
 * Portalled to the end of <body>, outside #root, as production placed it.
 */
export function ConsentBanner({ consent = defaultConsent }: { readonly consent?: ConsentApi }) {
  const open = useSyncExternalStore(consent.subscribe, consent.isBannerOpen, closed);
  if (!open) return null;
  return createPortal(<ConsentBannerView onAllow={consent.grant} onRefuse={consent.deny} />, document.body);
}
